import { EntitySubType, HAZARD_MARKERS, HazardType, MapboxLabelLayer, MapboxLayer } from '@/features';
import { i18n } from '@/locales';

const { t } = i18n.global;

export enum ToolbarMode {
  None = 'None',
  Filters = 'Filters',
  Overlays = 'Overlays',
}

export enum OverlayType {
  DistrictBoundaries = 'District boundaries',
  Weather = 'Weather',
}

export enum OverlaySubType {
  // District Boundaries
  DFESRegions = 'DFES regions',
  LocalGovernments = 'Local Governments',
  Pastoral = 'Pastoral',
  // Weather
  CycloneTrack = 'Cyclone track',
  RainRadar = 'Rain radar',
  ThunderstormTracker = 'Thunderstorm tracker',
  Satellite = 'Satellite',
  WindDirection = 'Wind direction and speed',
  FloodCatchments = 'Flood catchments',
}

// TODO: extract
export interface Dropdown {
  placeholder: string;
  sections: DropdownGroup[];
}
export interface DropdownGroup {
  label: string;
  items: DropdownItem[];

  hazardType?: HazardType;

  count?: number;
}
export interface DropdownItem {
  label: string;
  image?: string;
  overlay?: MapboxLayer;
  labelLayer?: MapboxLabelLayer;

  hazardType?: HazardType;
  hazardSubType?: (typeof EntitySubType)[keyof typeof EntitySubType][];

  count?: number;
}

export type DropdownSelected = Record<string, Record<string, boolean>>;

export const typeSearchPlaceholder = t('NKGbeys8C4dbBRHkTHE3l');

export const overlaySections: DropdownGroup[] = [
  {
    label: OverlayType.DistrictBoundaries,
    items: [
      { label: OverlaySubType.DFESRegions, overlay: MapboxLayer.dfesRegionsLayer, labelLayer: MapboxLabelLayer.dfesRegionsLayer },
      { label: OverlaySubType.LocalGovernments, overlay: MapboxLayer.lgaLayer, labelLayer: MapboxLabelLayer.lgaLayer },
      { label: OverlaySubType.Pastoral, overlay: MapboxLayer.pastoralLayer, labelLayer: MapboxLabelLayer.pastoralLayer },
    ],
  },
  {
    label: OverlayType.Weather,
    items: [
      { label: OverlaySubType.RainRadar, overlay: MapboxLayer.rainRadarLayer },
      { label: OverlaySubType.ThunderstormTracker, overlay: MapboxLayer.thunderstormTrackerLayer },
      { label: OverlaySubType.Satellite, overlay: MapboxLayer.weatherSatelliteLayer },
      { label: OverlaySubType.WindDirection, overlay: MapboxLayer.windDirectionLayer },
      { label: OverlaySubType.FloodCatchments, overlay: MapboxLayer.floodCatchmentLayer },
    ],
  },
];

export const layerSections = (): DropdownGroup[] => [
  {
    label: t('EDYpgcWMPmjxd6dXB1XWM'),
    items: [
      // Air Quality
      { label: t('aQ8rBmKn3xPL6cW4vFte'), image: HAZARD_MARKERS['ew-air-quality--advice'] },
      { label: t('aQ2mKtBn9xRpL5cW7vFe'), image: HAZARD_MARKERS['ew-air-quality--watch-and-act'] },
      { label: t('yR3nBvAirQEmgWarn1ng'), image: HAZARD_MARKERS['ew-air-quality--emergency-warning'] },

      // Amber Alert
      { label: t('amberAlertEmgWrn1ng'), image: HAZARD_MARKERS['ew-amber-alert--emergency-warning'] },

      // Arctic Outflow
      { label: t('arcticOutflowAdv'), image: HAZARD_MARKERS['ew-arctic-outflow--advice'] },
      { label: t('arcticOutflowWrn'), image: HAZARD_MARKERS['ew-arctic-outflow--watch-and-act'] },
      { label: t('arcticOutflowEmg'), image: HAZARD_MARKERS['ew-arctic-outflow--emergency-warning'] },

      // Avalanche
      { label: t('avalancheAdv'), image: HAZARD_MARKERS['ew-avalanche--advice'] },
      { label: t('avalancheWrn'), image: HAZARD_MARKERS['ew-avalanche--watch-and-act'] },
      { label: t('avalancheEmg'), image: HAZARD_MARKERS['ew-avalanche--emergency-warning'] },

      // Blizzard
      { label: t('bZ9rNmKt4xPL8cQ2vFwe'), image: HAZARD_MARKERS['ew-blizzard--advice'] },
      { label: t('bZ4mNtKr7xPL2cQ9vFwe'), image: HAZARD_MARKERS['ew-blizzard--watch-and-act'] },
      { label: t('mK7wBlzEmg3WrnLgnd5'), image: HAZARD_MARKERS['ew-blizzard--emergency-warning'] },

      // Blowing Snow
      { label: t('blowingSnowAdv'), image: HAZARD_MARKERS['ew-blowing-snow--advice'] },
      { label: t('blowingSnowWrn'), image: HAZARD_MARKERS['ew-blowing-snow--watch-and-act'] },
      { label: t('blowingSnowEmg'), image: HAZARD_MARKERS['ew-blowing-snow--emergency-warning'] },

      // Dangerous Person
      { label: t('dangerousPrsnWatchAct'), image: HAZARD_MARKERS['ew-dangerous-person--watch-and-act'] },

      // Dust Storm
      { label: t('dustStormAdv'), image: HAZARD_MARKERS['ew-dust-storm--advice'] },
      { label: t('dustStormWrn'), image: HAZARD_MARKERS['ew-dust-storm--watch-and-act'] },
      { label: t('dustStormEmg'), image: HAZARD_MARKERS['ew-dust-storm--emergency-warning'] },

      // Earthquake (graduated)
      { label: t('earthquakeAdv'), image: HAZARD_MARKERS['ew-earthquake--advice'] },
      { label: t('earthquakeWrn'), image: HAZARD_MARKERS['ew-earthquake--watch-and-act'] },
      { label: t('earthquakeEmg'), image: HAZARD_MARKERS['ew-earthquake--emergency-warning'] },

      // Extreme Cold
      { label: t('eC9mNtKr5xPL3cQ7vFwe'), image: HAZARD_MARKERS['ew-extreme-cold--advice'] },
      { label: t('eC3mNtKr8xPL4cQ6vFwe'), image: HAZARD_MARKERS['ew-extreme-cold--watch-and-act'] },
      { label: t('eC7mNtKr2xPL5cQ4vFwe'), image: HAZARD_MARKERS['ew-extreme-cold--emergency-warning'] },

      // Flash Flood
      { label: t('fF4mNtKr9xPL2cQ6vFwe'), image: HAZARD_MARKERS['ew-flash-flood--advice'] },
      { label: t('fF8mNtKr3xPL7cQ2vFwe'), image: HAZARD_MARKERS['ew-flash-flood--watch-and-act'] },
      { label: t('fF2mNtKr7xPL4cQ8vFwe'), image: HAZARD_MARKERS['ew-flash-flood--emergency-warning'] },

      // Frost
      { label: t('frostAdv'), image: HAZARD_MARKERS['ew-frost--advice'] },
      { label: t('frostWrn'), image: HAZARD_MARKERS['ew-frost--watch-and-act'] },
      { label: t('frostEmg'), image: HAZARD_MARKERS['ew-frost--emergency-warning'] },

      // Flood
      { label: t('aeqcLBdAPkMADz72_SEA-'), image: HAZARD_MARKERS['ew-flood--advice'] },
      { label: t('AVExRQkCRHvJCbrHdw-2u'), image: HAZARD_MARKERS['ew-flood--watch-and-act'] },
      { label: t('TUBdi7uw5EfrrE-joGAPN'), image: HAZARD_MARKERS['ew-flood--emergency-warning'] },

      // Freezing Rain
      { label: t('fR9mNtKr4xPL7cQ3vFwe'), image: HAZARD_MARKERS['ew-freezing-rain--advice'] },
      { label: t('fR2mNtKr8xPL4cQ9vFwe'), image: HAZARD_MARKERS['ew-freezing-rain--watch-and-act'] },
      { label: t('fR7mNtKr3xPL6cQ2vFwe'), image: HAZARD_MARKERS['ew-freezing-rain--emergency-warning'] },

      // Gale Wind
      { label: t('galeWindAdv'), image: HAZARD_MARKERS['ew-gale-wind--advice'] },
      { label: t('galeWindWrn'), image: HAZARD_MARKERS['ew-gale-wind--watch-and-act'] },
      { label: t('galeWindEmg'), image: HAZARD_MARKERS['ew-gale-wind--emergency-warning'] },

      // Generic (Other Warning)
      { label: t('kkqMSgk_8v7lQ8-HSvcaz'), image: HAZARD_MARKERS['ew-other-warning'] },
      { label: t('gW8mNtKr3xPL5cQ4vFwe'), image: HAZARD_MARKERS['ew-other-warning--watch-and-act'] },
      { label: t('gE4mNtKr7xPL2cQ8vFwe'), image: HAZARD_MARKERS['ew-other-warning--emergency-warning'] },

      // Hazardous Materials
      { label: t('hzCC7mNtKr4xPL3cQ8vF'), image: HAZARD_MARKERS['ew-hazmat-controlled-contained'] },
      { label: t('vgRtWCN9kxY1NQn2qsZah'), image: HAZARD_MARKERS['ew-hazmat-general-warning'] },

      // Heatwave
      { label: t('n_Fc8Zzqm_xkrt5O6sxQK'), image: HAZARD_MARKERS['ew-heatwave--advice'] },
      { label: t('6m4NUBo3vE4NDcu3xYmkf'), image: HAZARD_MARKERS['ew-heatwave--watch-and-act'] },
      { label: t('C7HMGIKAEZJ3EU6_2Nh3c'), image: HAZARD_MARKERS['ew-heatwave--emergency-warning'] },

      // High Heat and Humidity
      { label: t('highHeatHumidityAdv'), image: HAZARD_MARKERS['ew-high-heat-and-humidity--advice'] },
      { label: t('highHeatHumidityWrn'), image: HAZARD_MARKERS['ew-high-heat-and-humidity--watch-and-act'] },
      { label: t('highHeatHumidityEmg'), image: HAZARD_MARKERS['ew-high-heat-and-humidity--emergency-warning'] },

      // Hurricane
      { label: t('hurricaneAdv'), image: HAZARD_MARKERS['ew-hurricane--advice'] },
      { label: t('hurricaneWrn'), image: HAZARD_MARKERS['ew-hurricane--watch-and-act'] },
      { label: t('hurricaneEmg'), image: HAZARD_MARKERS['ew-hurricane--emergency-warning'] },

      // Hurricane Force Wind
      { label: t('hurricaneForceWindAdv'), image: HAZARD_MARKERS['ew-hurricane-force-wind--advice'] },
      { label: t('hurricaneForceWindWrn'), image: HAZARD_MARKERS['ew-hurricane-force-wind--watch-and-act'] },
      { label: t('hurricaneForceWindEmg'), image: HAZARD_MARKERS['ew-hurricane-force-wind--emergency-warning'] },

      // Human Pandemic
      { label: t('hPA6mNtKr7xPL2cQ9vFw'), image: HAZARD_MARKERS['ew-advice'] },
      { label: t('_eCnnH9IK21WP1qEYxCnA'), image: HAZARD_MARKERS['ew-alert'] },
      { label: t('hPE8mNtKr3xPL5cQ4vFw'), image: HAZARD_MARKERS['ew-community-information'] },

      // Ice Jam
      { label: t('iJ4mNtKr6xPL3cQ8vFwe'), image: HAZARD_MARKERS['ew-ice-jam--advice'] },
      { label: t('iJ7mNtKr2xPL9cQ3vFwe'), image: HAZARD_MARKERS['ew-ice-jam--watch-and-act'] },
      { label: t('iJ3mNtKr8xPL4cQ7vFwe'), image: HAZARD_MARKERS['ew-ice-jam--emergency-warning'] },

      // Lahar
      { label: t('laharAdv'), image: HAZARD_MARKERS['ew-lahar--advice'] },
      { label: t('laharWrn'), image: HAZARD_MARKERS['ew-lahar--watch-and-act'] },
      { label: t('laharEmg'), image: HAZARD_MARKERS['ew-lahar--emergency-warning'] },

      // Landslide
      { label: t('landslideAdv'), image: HAZARD_MARKERS['ew-landslide--advice'] },
      { label: t('landslideWrn'), image: HAZARD_MARKERS['ew-landslide--watch-and-act'] },
      { label: t('landslideEmg'), image: HAZARD_MARKERS['ew-landslide--emergency-warning'] },

      // Lava Flow
      { label: t('lavaFlowAdv'), image: HAZARD_MARKERS['ew-lava-flow--advice'] },
      { label: t('lavaFlowWrn'), image: HAZARD_MARKERS['ew-lava-flow--watch-and-act'] },
      { label: t('lavaFlowEmg'), image: HAZARD_MARKERS['ew-lava-flow--emergency-warning'] },

      // Lake Flooding
      { label: t('lF4mNtKr6xPL2cQ9vFwe'), image: HAZARD_MARKERS['ew-lake-flooding--advice'] },
      { label: t('lF8mNtKr3xPL7cQ2vFwe'), image: HAZARD_MARKERS['ew-lake-flooding--watch-and-act'] },
      { label: t('lF2mNtKr9xPL4cQ7vFwe'), image: HAZARD_MARKERS['ew-lake-flooding--emergency-warning'] },

      // Magnetic Storm
      { label: t('magneticStormAdv'), image: HAZARD_MARKERS['ew-magnetic-storm--advice'] },
      { label: t('magneticStormWrn'), image: HAZARD_MARKERS['ew-magnetic-storm--watch-and-act'] },
      { label: t('magneticStormEmg'), image: HAZARD_MARKERS['ew-magnetic-storm--emergency-warning'] },

      // Meteorite
      { label: t('meteoriteAdv'), image: HAZARD_MARKERS['ew-meteorite--advice'] },
      { label: t('meteoriteWrn'), image: HAZARD_MARKERS['ew-meteorite--watch-and-act'] },
      { label: t('meteoriteEmg'), image: HAZARD_MARKERS['ew-meteorite--emergency-warning'] },

      // Missing Vulnerable Person
      { label: t('missingVulnPrsnWatchAct'), image: HAZARD_MARKERS['ew-missing-vulnerable-person--watch-and-act'] },

      // Power Outage
      { label: t('uiKiGZPB1d4_rSIZ4yB4h'), image: HAZARD_MARKERS['ew-power-outage'] },
      { label: t('pO3mNtKr8xPL7cQ2vFwe'), image: HAZARD_MARKERS['ew-power-outage--watch-and-act'] },
      { label: t('pO7mNtKr4xPL3cQ8vFwe'), image: HAZARD_MARKERS['ew-power-outage--emergency-warning'] },

      // Pyroclastic Flow
      { label: t('pyroclasticFlowAdv'), image: HAZARD_MARKERS['ew-pyroclastic-flow--advice'] },
      { label: t('pyroclasticFlowWrn'), image: HAZARD_MARKERS['ew-pyroclastic-flow--watch-and-act'] },
      { label: t('pyroclasticFlowEmg'), image: HAZARD_MARKERS['ew-pyroclastic-flow--emergency-warning'] },

      // Pyroclastic Surge
      { label: t('pyroclasticSurgeAdv'), image: HAZARD_MARKERS['ew-pyroclastic-surge--advice'] },
      { label: t('pyroclasticSurgeWrn'), image: HAZARD_MARKERS['ew-pyroclastic-surge--watch-and-act'] },
      { label: t('pyroclasticSurgeEmg'), image: HAZARD_MARKERS['ew-pyroclastic-surge--emergency-warning'] },

      // Rainfall
      { label: t('rainfallAdv'), image: HAZARD_MARKERS['ew-rainfall--advice'] },
      { label: t('rainfallWrn'), image: HAZARD_MARKERS['ew-rainfall--watch-and-act'] },
      { label: t('rainfallEmg'), image: HAZARD_MARKERS['ew-rainfall--emergency-warning'] },

      // River Flooding
      { label: t('rF6mNtKr4xPL8cQ2vFwe'), image: HAZARD_MARKERS['ew-river-flooding--advice'] },
      { label: t('rF3mNtKr7xPL4cQ9vFwe'), image: HAZARD_MARKERS['ew-river-flooding--watch-and-act'] },
      { label: t('rF8mNtKr2xPL5cQ7vFwe'), image: HAZARD_MARKERS['ew-river-flooding--emergency-warning'] },

      // Seasonal Overland Flooding
      { label: t('sO9mNtKr4xPL3cQ7vFwe'), image: HAZARD_MARKERS['ew-seasonal-overland-flooding--advice'] },
      { label: t('sO2mNtKr8xPL4cQ6vFwe'), image: HAZARD_MARKERS['ew-seasonal-overland-flooding--watch-and-act'] },
      { label: t('sO7mNtKr3xPL5cQ8vFwe'), image: HAZARD_MARKERS['ew-seasonal-overland-flooding--emergency-warning'] },

      // Severe Thunderstorm / Lightning
      { label: t('IK6VbGlK60AkArf6jPmPA'), image: HAZARD_MARKERS['ew-storm--advice'] },
      { label: t('BZfpyIc1qEsxGAmzXvKdS'), image: HAZARD_MARKERS['ew-storm--watch-and-act'] },
      { label: t('c9B7DCSs9bDX-aeGdTV8s'), image: HAZARD_MARKERS['ew-storm--emergency-warning'] },

      // Severe Winter Weather
      { label: t('sW2mNtKr6xPL4cQ8vFwe'), image: HAZARD_MARKERS['ew-severe-winter-weather--advice'] },
      { label: t('sW8mNtKr3xPL5cQ4vFwe'), image: HAZARD_MARKERS['ew-severe-winter-weather--watch-and-act'] },
      { label: t('sW4mNtKr7xPL2cQ9vFwe'), image: HAZARD_MARKERS['ew-severe-winter-weather--emergency-warning'] },

      // Silver Alert
      { label: t('silverAlertWatchAct'), image: HAZARD_MARKERS['ew-silver-alert--watch-and-act'] },

      // Smoke
      { label: t('smA3mNtKr8xPL5cQ7vFw'), image: HAZARD_MARKERS['ew-smoke-alert'] },

      // Snowfall
      { label: t('snowfallAdv'), image: HAZARD_MARKERS['ew-snowfall--advice'] },
      { label: t('snowfallWrn'), image: HAZARD_MARKERS['ew-snowfall--watch-and-act'] },
      { label: t('snowfallEmg'), image: HAZARD_MARKERS['ew-snowfall--emergency-warning'] },

      // Special Ice
      { label: t('specialIceAdv'), image: HAZARD_MARKERS['ew-special-ice--advice'] },
      { label: t('specialIceWrn'), image: HAZARD_MARKERS['ew-special-ice--watch-and-act'] },
      { label: t('specialIceEmg'), image: HAZARD_MARKERS['ew-special-ice--emergency-warning'] },

      // Special Marine
      { label: t('specialMarineAdv'), image: HAZARD_MARKERS['ew-special-marine--advice'] },
      { label: t('specialMarineWrn'), image: HAZARD_MARKERS['ew-special-marine--watch-and-act'] },
      { label: t('specialMarineEmg'), image: HAZARD_MARKERS['ew-special-marine--emergency-warning'] },

      // Storm Force Wind
      { label: t('stormForceWindAdv'), image: HAZARD_MARKERS['ew-storm-force-wind--advice'] },
      { label: t('stormForceWindWrn'), image: HAZARD_MARKERS['ew-storm-force-wind--watch-and-act'] },
      { label: t('stormForceWindEmg'), image: HAZARD_MARKERS['ew-storm-force-wind--emergency-warning'] },

      // Storm Surge
      { label: t('ssAdvKey56vXmNpL2cKR'), image: HAZARD_MARKERS['ew-storm-surge--advice'] },
      { label: t('ssWrnKey34mBnKtR7cQL'), image: HAZARD_MARKERS['ew-storm-surge--watch-and-act'] },
      { label: t('ssEmgKey12bLxT9wKR4p'), image: HAZARD_MARKERS['ew-storm-surge--emergency-warning'] },

      // Structure Fire
      { label: t('sfAC6mNtKr2xPL9cQ4vF'), image: HAZARD_MARKERS['ew-structure-fire-contained-controlled'] },
      { label: t('4M6F61FhO_MnwTUq633xm'), image: HAZARD_MARKERS['ew-structure-fire-warning'] },

      // Tornado
      { label: t('tN6mNtKr2xPL9cQ4vFwe'), image: HAZARD_MARKERS['ew-tornado--advice'] },
      { label: t('tN3mNtKr8xPL4cQ7vFwe'), image: HAZARD_MARKERS['ew-tornado--watch-and-act'] },
      { label: t('tN7mNtKr3xPL5cQ2vFwe'), image: HAZARD_MARKERS['ew-tornado--emergency-warning'] },

      // Tropical Storm
      { label: t('tropicalStormAdv'), image: HAZARD_MARKERS['ew-tropical-storm--advice'] },
      { label: t('tropicalStormWrn'), image: HAZARD_MARKERS['ew-tropical-storm--watch-and-act'] },
      { label: t('tropicalStormEmg'), image: HAZARD_MARKERS['ew-tropical-storm--emergency-warning'] },

      // Tsunami (graduated)
      { label: t('tsunamiAdv'), image: HAZARD_MARKERS['ew-tsunami--advice'] },
      { label: t('tsunamiWrn'), image: HAZARD_MARKERS['ew-tsunami--watch-and-act'] },
      { label: t('tsunamiEmg'), image: HAZARD_MARKERS['ew-tsunami--emergency-warning'] },

      // Volcanic Ash Cloud
      { label: t('volcanicAshCloudAdv'), image: HAZARD_MARKERS['ew-volcanic-ash-cloud--advice'] },
      { label: t('volcanicAshCloudWrn'), image: HAZARD_MARKERS['ew-volcanic-ash-cloud--watch-and-act'] },
      { label: t('volcanicAshCloudEmg'), image: HAZARD_MARKERS['ew-volcanic-ash-cloud--emergency-warning'] },

      // Volcano
      { label: t('volcanoAdv'), image: HAZARD_MARKERS['ew-volcano--advice'] },
      { label: t('volcanoWrn'), image: HAZARD_MARKERS['ew-volcano--watch-and-act'] },
      { label: t('volcanoEmg'), image: HAZARD_MARKERS['ew-volcano--emergency-warning'] },

      // Waterspout
      { label: t('waterspoutAdv'), image: HAZARD_MARKERS['ew-waterspout--advice'] },
      { label: t('waterspoutWrn'), image: HAZARD_MARKERS['ew-waterspout--watch-and-act'] },
      { label: t('waterspoutEmg'), image: HAZARD_MARKERS['ew-waterspout--emergency-warning'] },

      // Wildfire
      { label: t('XrcnmbTXkiOSVBmVMafeG'), image: HAZARD_MARKERS['ew-bushfire--advice'] },
      { label: t('fBwJNbZpRX1wreEHZIxt-'), image: HAZARD_MARKERS['ew-bushfire--watch-and-act'] },
      { label: t('RxaL17SqXZyV9BAOpNptR'), image: HAZARD_MARKERS['ew-bushfire--emergency-warning'] },

      // Windstorm
      { label: t('opozjowRUuAXZesOr1O_N'), image: HAZARD_MARKERS['ew-damaging-winds--advice'] },
      { label: t('e8sAGXp-1_bII3V9qPE3M'), image: HAZARD_MARKERS['ew-damaging-winds--watch-and-act'] },
      { label: t('dl0sCf9OcvKlslgcb3DmE'), image: HAZARD_MARKERS['ew-damaging-winds--emergency-warning'] },
    ],
  },
  {
    label: t('x9pt5ALdrxsYA3DsOLgm6'),
    items: [
      { label: t('otherActiveAlarmLbl'), image: HAZARD_MARKERS['ew-other-active-alarm'] }, // Active Alarm
      { label: t('otherAircraftCrashLbl'), image: HAZARD_MARKERS['ew-other-aircraft-crash'] }, // Aircraft Crash
      { label: t('otherAnimalDiseaseLbl'), image: HAZARD_MARKERS['ew-other-animal-disease'] }, // Animal Disease
      { label: t('otherAnimalFeedLbl'), image: HAZARD_MARKERS['ew-other-animal-feed'] }, // Animal Feed
      { label: t('otherBiologicalHazardLbl'), image: HAZARD_MARKERS['ew-other-biological-hazard'] }, // Biological Hazard
      { label: t('bI3mNtKr8xPL5cQ4vFwe'), image: HAZARD_MARKERS['ew-blizzard'] }, // Blizzard
      { label: t('otherBurnOffLbl'), image: HAZARD_MARKERS['ew-other-burn-off'] }, // Burn Off
      { label: t('otherChemicalHazardLbl'), image: HAZARD_MARKERS['ew-other-chemical-hazard'] }, // Chemical Hazard
      { label: t('otherCivilEmergencyLbl'), image: HAZARD_MARKERS['ew-other-civil-emergency'] }, // Civil Emergency
      { label: t('otherDamOverflowLbl'), image: HAZARD_MARKERS['ew-other-dam-overflow'] }, // Dam Overflow
      { label: t('otherDrinkingWaterLbl'), image: HAZARD_MARKERS['ew-other-drinking-water'] }, // Drinking Water
      { label: t('otherExplosiveHazardLbl'), image: HAZARD_MARKERS['ew-other-explosive-hazard'] }, // Explosive Hazard
      { label: t('otherFireLbl'), image: HAZARD_MARKERS['ew-other-fire'] }, // Fire
      { label: t('fI2mNtKr7xPL4cQ9vFwe'), image: HAZARD_MARKERS['ew-flood'] }, // Flood
      { label: t('otherFloodLbl'), image: HAZARD_MARKERS['ew-other-flood'] }, // Flood (Other)
      { label: t('otherFuelLeakLbl'), image: HAZARD_MARKERS['ew-other-fuel-leak'] }, // Fuel Leak
      { label: t('nXT1Of0lxhlO9C2JvSJuw'), image: HAZARD_MARKERS['ew-other-incident'] }, // Generic
      { label: t('fB1MbPSnjVc_XvNwBuJOT'), image: HAZARD_MARKERS['ew-hazardous-materials'] }, // Hazardous Materials
      { label: t('otherHazardRoadCondLbl'), image: HAZARD_MARKERS['ew-other-hazardous-road-conditions'] }, // Hazardous Road Conditions
      { label: t('otherHighWaterLevelLbl'), image: HAZARD_MARKERS['ew-other-high-water-level'] }, // High Water Level
      { label: t('hEi4mNtKr8xPL5cQ7vFw'), image: HAZARD_MARKERS['ew-human-epidemic'] }, // Human Epidemic
      { label: t('ry9wjOHWz61gShaxRgrU4'), image: HAZARD_MARKERS['ew-human-pandemic'] }, // Human Pandemic
      { label: t('otherIndustrialFireLbl'), image: HAZARD_MARKERS['ew-other-industrial-fire'] }, // Industrial Fire
      { label: t('otherInfectiousDiseaseLbl'), image: HAZARD_MARKERS['ew-other-infectious-disease'] }, // Infectious Disease
      { label: t('otherMarineSecurityLbl'), image: HAZARD_MARKERS['ew-other-marine-security'] }, // Marine Security
      { label: t('otherMotorVehAccLbl'), image: HAZARD_MARKERS['ew-other-motor-vehicle-accident'] }, // Motor Vehicle Accident
      { label: t('otherNauticalIncidentLbl'), image: HAZARD_MARKERS['ew-other-nautical-incident'] }, // Nautical Incident
      { label: t('otherNoticeToAirmenLbl'), image: HAZARD_MARKERS['ew-other-notice-to-airmen'] }, // Notice to Airmen
      { label: t('otherPlantInfDiseaseLbl'), image: HAZARD_MARKERS['ew-other-plant-infectious-disease'] }, // Plant Infectious Disease
      { label: t('otherPrescribedBurnLbl'), image: HAZARD_MARKERS['ew-other-prescribed-burn'] }, // Prescribed Burn
      { label: t('otherPublicEventLbl'), image: HAZARD_MARKERS['ew-other-public-event'] }, // Public Event
      { label: t('otherRadiologicalHazardLbl'), image: HAZARD_MARKERS['ew-other-radiological-hazard'] }, // Radiological Hazard
      { label: t('otherRapidCoastalLeadsLbl'), image: HAZARD_MARKERS['ew-other-rapid-closing-of-coastal-leads'] }, // Rapid Closing of Coastal Leads
      { label: t('otherReportOfSmokeLbl'), image: HAZARD_MARKERS['ew-other-report-of-smoke'] }, // Report of Smoke
      { label: t('a8ZrH1ps-FyZxCdbyxbzk'), image: HAZARD_MARKERS['ew-rescue'] }, // Rescue
      { label: t('otherRoadCrashLbl'), image: HAZARD_MARKERS['ew-other-road-crash'] }, // Road Crash
      { label: t('rI4mNtKr9xPL3cQ7vFwe'), image: HAZARD_MARKERS['ew-other-incident'] }, // Road Incident
      { label: t('rW6mNtKr2xPL8cQ4vFwe'), image: HAZARD_MARKERS['ew-other-road-work'] }, // Road Work
      { label: t('otherSchoolLockdownLbl'), image: HAZARD_MARKERS['ew-other-school-lockdown'] }, // School Lockdown
      { label: t('otherServiceFacilityLbl'), image: HAZARD_MARKERS['ew-other-service-or-facility'] }, // Service or Facility
      { label: t('otherSmellOfGasLbl'), image: HAZARD_MARKERS['ew-other-smell-of-gas'] }, // Smell of Gas
      { label: t('sI6mNtKr3xPL8cQ2vFwe'), image: HAZARD_MARKERS['ew-storm'] }, // Storm
      { label: t('sDI4mNtKr9xPL3cQ7vFw'), image: HAZARD_MARKERS['ew-storm-damage'] }, // Storm Damage
      { label: t('otherStructureDamageLbl'), image: HAZARD_MARKERS['ew-other-structure-damage'] }, // Structure Damage
      { label: t('VXrGaiFmQF-aaah1McCVE'), image: HAZARD_MARKERS['ew-structure-fire'] }, // Structure Fire
      { label: t('otherTerrorismLbl'), image: HAZARD_MARKERS['ew-other-terrorism'] }, // Terrorism
      { label: t('tI7mNtKr2xPL5cQ4vFwe'), image: HAZARD_MARKERS['ew-tornado'] }, // Tornado
      { label: t('otherTrainAccLbl'), image: HAZARD_MARKERS['ew-other-train-accident'] }, // Train Accident
      { label: t('otherUrbanFireLbl'), image: HAZARD_MARKERS['ew-other-urban-fire'] }, // Urban Fire
      { label: t('otherVehicleFireLbl'), image: HAZARD_MARKERS['ew-other-vehicle-fire'] }, // Vehicle Fire
      { label: t('otherWaterSupplyLbl'), image: HAZARD_MARKERS['ew-other-water-supply'] }, // Water Supply
      { label: t('5hRAby0mkv1XUKMYWzBRM'), image: HAZARD_MARKERS['ew-bushfire'] }, // Wildfire
      { label: t('wI3mNtKr8xPL5cQ2vFwe'), image: HAZARD_MARKERS['ew-damaging-winds'] }, // Windstorm
    ],
  },
  {
    label: t('cEIcon9yKpR7jLs2FwLgnd'),
    items: [
      { label: t('4pxFgiGCG3yT9K1woJ_Ll'), image: HAZARD_MARKERS['ew-evacuation-supports'] },
      { label: t('gC3nTrKm8xPL5cQ2vFwe'), image: HAZARD_MARKERS['ew-generic-centre'] },
      { label: t('zZ2Tg7mII5zI4-lYyceQg'), image: HAZARD_MARKERS['ew-reception-centre'] },
    ],
  },
  {
    label: t('clsIcon3xPL8cQ2vFwe9'),
    items: [
      { label: t('airportClosureLbl'), image: HAZARD_MARKERS['ew-airport-closure'] },
      { label: t('airspaceClosureLbl'), image: HAZARD_MARKERS['ew-airspace-closure'] },
      { label: t('otherBridgeClosureLbl'), image: HAZARD_MARKERS['ew-other-bridge-closure'] },
      { label: t('4viyfb2ojOTQlBKPwLDeX'), image: HAZARD_MARKERS['ew-facility-or-park-closure'] },
      { label: t('otherClosureLbl'), image: HAZARD_MARKERS['ew-other-closure'] },
      { label: t('pC5mNtKr2xPL8cQ9vFwe'), image: HAZARD_MARKERS['ew-park-closure'] },
      { label: t('qL9JfyMYjR3sSxVSVhO5l'), image: HAZARD_MARKERS['ew-road-closure'] },
      { label: t('zOSJy1oRtXyCmEyulLrUu'), image: HAZARD_MARKERS['ew-school-closure'] },
    ],
  },
];
